import { ContentScope } from './contentScope';
/**
 * Render preview/approve API calls (Phase 5 §2, Journey 2).
 * Kept out of AppContext: render review is a flow-scoped concern, not
 * app-global state — same pattern as audio/sessionApi.ts.
 */

import { ApiError, requestJson } from './http';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export type RenderStatus =
  | 'queued'
  | 'rendering'
  | 'machine_passed'
  | 'parent_approved'
  | 'regenerating'
  | 'needs_review'
  | 'internal_reviewed'
  | 'failed';

export interface RenderListItem {
  render_id: string;
  story_id: string;
  voice_id: string;
  household_id?: string | null;
  child_slug: string | null;
  listener_id: string | null;
  listener_name: string | null;
  status: RenderStatus;
  failure_code?: string | null;
  output_seconds?: number | null;
  estimated_duration_minutes?: number | null;
  created_at: string;
}

export interface RenderSegment {
  segment_id: number;
  start_ms: number;
  end_ms: number;
  text: string;
}

export interface RenderDetail {
  content_scope?: ContentScope | null;
  offline_access_expires_at?: string | null;
  render_id: string;
  status: RenderStatus;
  failure_code?: string | null;
  story_id: string;
  story_title: string;
  voice_id: string;
  household_id?: string | null;
  child_slug: string | null;
  listener_id: string | null;
  listener_name: string | null;
  engine: string | null;
  output_seconds?: number | null;
  estimated_duration_minutes?: number | null;
  audio_url: string | null;
  review_samples: ReviewSample[];
  has_soundbed?: boolean;
  speech_only_audio_url?: string | null;
  soundbed_attribution?: string | null;
  soundbed_review_assignment_id?: string | null;
  soundbed_assignment_state?: 'draft' | 'listening_review' | 'approved' | 'retired' | null;
  segments: RenderSegment[];
  regens_today: number;
  regen_limit: number;
}

export interface RegenerateResult {
  render_id: string;
  status: RenderStatus;
  audio_url: string | null;
  speech_only_audio_url?: string | null;
  version?: number;
  output_seconds?: number | null;
  segments: RenderSegment[];
  regens_today: number;
  regen_limit: number;
}

export interface EnqueueResult {
  job_id: string | null;
  status: 'queued' | 'already_rendering' | 'existing';
  render_id?: string;
}

export interface InternalClassicReviewListItem {
  render_id: string;
  story_id: string;
  voice_id: string;
  listener_id: string | null;
  status: RenderStatus;
  output_seconds: number | null;
  created_at: string;
}

export interface InternalClassicReviewDetail extends InternalClassicReviewListItem {
  story_title: string;
  listener_name: string | null;
  audio_url: string | null;
  scorecard: Record<string, unknown> | null;
  review_samples: ReviewSample[];
}

export interface ReviewSample {
  sample_id: 'intro' | 'opening' | 'middle' | 'closing' | 'goodnight';
  label: string;
  start_seconds: number;
  end_seconds: number;
  required_seconds: number;
  kind: 'bookend' | 'story';
}

/** Kick off a gated pipeline render (performance -> chunks -> gates ->
 * mastered story with verbatim bookends). The result appears in /renders
 * as 'rendering' and flips to machine_passed for parent review. */
export async function enqueueRender(
  storyId: string,
  voiceId: string,
  listenerId?: string,
  signal?: AbortSignal,
  giftEntitlementId?: string,
  subscriptionId?: string,
  expectedContentSha256?: string
): Promise<EnqueueResult> {
  const params = new URLSearchParams({ story_id: storyId, voice_id: voiceId });
  if (listenerId) params.set('listener_id', listenerId);
  if (giftEntitlementId && subscriptionId) throw new Error('Select exactly one entitlement source');
  if (giftEntitlementId) params.set('gift_entitlement_id', giftEntitlementId);
  if (subscriptionId) params.set('subscription_id', subscriptionId);
  if (expectedContentSha256) params.set('expected_content_sha256', expectedContentSha256);
  return requestJson<EnqueueResult>(`${API_BASE_URL}/renders?${params}`, {
    method: 'POST',
    headers: authHeaders(),
    signal,
  });
}

export async function retryRender(renderId: string): Promise<EnqueueResult> {
  return requestJson<EnqueueResult>(
    `${API_BASE_URL}/renders/${encodeURIComponent(renderId)}/retry`,
    {
      method: 'POST',
      headers: authHeaders(),
    }
  );
}

/** Create an unpublished, operator-only review render. It never appears in
 * the family render list and cannot be approved for child playback. */
export async function enqueueInternalClassicReview(
  storyId: string,
  voiceId: string,
  listenerId: string
): Promise<EnqueueResult> {
  const params = new URLSearchParams({
    story_id: storyId,
    voice_id: voiceId,
    listener_id: listenerId,
  });
  return requestJson<EnqueueResult>(`${API_BASE_URL}/renders/internal-classic-review?${params}`, {
    method: 'POST',
    headers: authHeaders(),
  });
}

export async function fetchInternalClassicReviews(): Promise<InternalClassicReviewListItem[]> {
  return requestJson<InternalClassicReviewListItem[]>(`${API_BASE_URL}/renders/internal-classic-review`, {
    headers: authHeaders(),
  });
}

export async function fetchInternalClassicReview(
  renderId: string
): Promise<InternalClassicReviewDetail> {
  return requestJson<InternalClassicReviewDetail>(
    `${API_BASE_URL}/renders/internal-classic-review/${encodeURIComponent(renderId)}`,
    { headers: authHeaders() }
  );
}

export async function completeInternalClassicReview(
  renderId: string,
  evidence: {
    evidence_version: 2;
    listened_seconds: number;
    completion_ratio: number;
    intro_listened: boolean;
    goodnight_listened: boolean;
    reviewed_sample_ids: string[];
  }
): Promise<{ render_id: string; status: RenderStatus; two_listener_bookend_proof: boolean }> {
  return requestJson<{ render_id: string; status: RenderStatus; two_listener_bookend_proof: boolean }>(
    `${API_BASE_URL}/renders/internal-classic-review/${encodeURIComponent(renderId)}/complete`,
    {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(evidence),
    }
  );
}

export async function makeInternalClassicReviewAvailable(
  renderId: string
): Promise<{ source_render_id: string; render_id: string; status: RenderStatus; reused_audio: boolean }> {
  return requestJson<{ source_render_id: string; render_id: string; status: RenderStatus; reused_audio: boolean }>(
    `${API_BASE_URL}/renders/internal-classic-review/${encodeURIComponent(renderId)}/make-available`,
    { method: 'POST', headers: authHeaders() }
  );
}

/** Child-facing callers pass status='parent_approved' — the hard Phase 5
 * rule is enforced by the server-side filter, not by UI hiding. */
export interface RenderQuery {
  status?: RenderStatus;
  householdId?: string;
  listenerId?: string;
  voiceId?: string;
  signal?: AbortSignal;
}

export async function fetchRenders(query: RenderQuery = {}): Promise<RenderListItem[]> {
  const params = new URLSearchParams();
  if (query.status) params.set('status', query.status);
  if (query.householdId) params.set('household_id', query.householdId);
  if (query.listenerId) params.set('listener_id', query.listenerId);
  if (query.voiceId) params.set('voice_id', query.voiceId);
  const qs = params.toString();
  return requestJson<RenderListItem[]>(`${API_BASE_URL}/renders${qs ? `?${qs}` : ''}`, {
    headers: authHeaders(),
    signal: query.signal,
  });
}

export async function fetchRender(
  renderId: string,
  playback = false,
  signal?: AbortSignal
): Promise<RenderDetail> {
  const suffix = playback ? '?playback=true' : '';
  return requestJson<RenderDetail>(`${API_BASE_URL}/renders/${encodeURIComponent(renderId)}${suffix}`, {
    headers: authHeaders(),
    signal,
  });
}

export async function approveRender(
  renderId: string,
  evidence: {
    evidence_version: 2;
    listened_seconds: number;
    completion_ratio: number;
    intro_listened: boolean;
    goodnight_listened: boolean;
    reviewed_sample_ids: string[];
  }
): Promise<{ render_id: string; status: RenderStatus }> {
  return requestJson<{ render_id: string; status: RenderStatus }>(`${API_BASE_URL}/renders/${encodeURIComponent(renderId)}/approve`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(evidence),
  });
}

/** Throws RegenLimitError when the server's daily rate limit (429) is hit. */
export class RegenLimitError extends Error {}

export async function regenerateSegment(
  renderId: string,
  segmentId: number
): Promise<RegenerateResult> {
  try {
    return await requestJson<RegenerateResult>(
      `${API_BASE_URL}/renders/${encodeURIComponent(renderId)}/segments/${segmentId}/regenerate`,
      { method: 'POST', headers: authHeaders() }
    );
  } catch (error) {
    if (error instanceof ApiError && error.code === 'rate_limited') {
      throw new RegenLimitError();
    }
    throw error;
  }
}
